import React, { useEffect, useState } from "react";
import { Button, Typography, Card, Alert, Tag, Row, Col, Space } from "antd";
import { ReloadOutlined, CheckCircleOutlined, MinusCircleOutlined } from "@ant-design/icons";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import {
  listModels,
  setCurrentModel as setCurrentModelService,
} from "./assistant-service";

const ModelView = () => {
  const { apiCall } = useApiCall();
  const { errorToast, successToast } = useToast();

  const [models, setModels] = useState([]);
  const [currentModel, setCurrentModel] = useState("");
  const [loading, setLoading] = useState(false);
  const [settingModel, setSettingModel] = useState(false);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await listModels(apiCall);

      if (response && response.models) {
        setModels(response.models);
        setCurrentModel(response.current || "");
        successToast("Models refreshed successfully");
      } else {
        setModels([]);
        setCurrentModel("");
      }
      setLoading(false);
    } catch (error) {
      errorToast("Failed to fetch models");
      setLoading(false);
    }
  };

  const handleModelSelect = async model => {
    if (model === currentModel) {
      return; // Already selected
    }

    try {
      setSettingModel(true);
      const response = await setCurrentModelService(apiCall, model);

      if (response) {
        setCurrentModel(model);
        successToast(`Model changed to ${model}`);
      }
      setSettingModel(false);
    } catch (error) {
      errorToast("Failed to set model");
      setSettingModel(false);
    }
  };

  return (
    <div style={{ flexGrow: 1, padding: 16 }}>
      <Row gutter={24}>
        {/* Header */}
        <Col span={24}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <Typography.Title level={3}>Model Management</Typography.Title>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchModels}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>
        </Col>

        {/* Current Model Info */}
        {currentModel && (
          <Col span={24}>
            <Alert
              message={
                <Typography.Text>
                <strong>Current Model:</strong> {currentModel}
                </Typography.Text>
              }
              type="info"
              style={{ marginBottom: 16 }}
            />
          </Col>
        )}

        {/* Models List */}
        <Col span={24}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <LoadingSpinner />
            </div>
          ) : (
            <Row gutter={16}>
              {models.length === 0 ? (
                <Col span={24}>
                  <Typography.Text type="secondary" style={{ display: "block", textAlign: "center" }}>
                    No models available
                  </Typography.Text>
                </Col>
              ) : (
                models.map((model, index) => {
                  const isCurrentModel = model === currentModel;
                  return (
                    <Col xs={24} sm={12} md={8} key={index}>
                      <Card
                        style={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          border: isCurrentModel
                            ? "2px solid #1890ff"
                            : "1px solid #e0e0e0",
                          cursor: isCurrentModel ? "default" : "pointer",
                        }}
                        onClick={() =>
                          !isCurrentModel &&
                          !settingModel &&
                          handleModelSelect(model)
                        }
                        bodyStyle={{ flex: 1, display: "flex", flexDirection: "column" }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                            {isCurrentModel ? (
                              <CheckCircleOutlined style={{ color: "#1890ff", marginRight: 8 }} />
                            ) : (
                              <MinusCircleOutlined style={{ color: "#d9d9d9", marginRight: 8 }} />
                            )}
                            <Typography.Title level={5} style={{ margin: 0 }}>
                              {model}
                            </Typography.Title>
                          </div>
                          {isCurrentModel && (
                            <Tag color="blue" style={{ marginTop: 8 }}>Current</Tag>
                          )}
                        </div>
                        <div style={{ marginTop: 16 }}>
                          <Button
                            size="small"
                            type={isCurrentModel ? "default" : "primary"}
                            disabled={isCurrentModel || settingModel}
                            onClick={e => {
                              e.stopPropagation();
                              handleModelSelect(model);
                            }}
                            block
                          >
                            {isCurrentModel
                              ? "Active"
                              : settingModel
                              ? "Setting..."
                              : "Select"}
                          </Button>
                        </div>
                      </Card>
                    </Col>
                  );
                })
              )}
            </Row>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ModelView;
